import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import { useFormik, FormikValues } from "formik";
import * as yup from 'yup';
import { toast } from "sonner";
import moment from "moment";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { Button } from "rizzui";
import PageHeader from "../../../layout/shared/page-header";
import { MdErrorOutline } from "react-icons/md";
import { LuClipboardList } from "react-icons/lu";
import { PiBird } from "react-icons/pi";
import { TbRosetteDiscountCheck } from "react-icons/tb";
import { CensusService } from "../../../services/census/censusService";
import { DocumentsService } from "../../../services/document/documentsService";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useCensusFormContext } from "../../census/components/provider/CensusFormProvider";
import { createLineValidationSchema, generalDataValidationSchemaOFC, SuperAdminSchemaOFC, TabData } from "../../census/components/types/CensusSchemas"; 
import CensusLineForm from "../../census/components/CensusLineForm";
import { normalizeWindFieldsForForm } from "../../../utils/censusLineUtils";
import OfcGeneralDataForm from "../components/OfcGeneralDataForm";

const getInitialLineValues = () => ({
  specieId: null,
  turbineId:null,
  windDirection: null,
  windSpeed: null,
  cloudCoverage: null,
  visibility: null,
  numIndividuals: null,
  aeroStatus: 'encendido',
  altitudLevel: null,
  distanceToAero: null,
  riskDetected: null,
  isOfc: 'false',
  dirOrigen: null,
  dirDest: null,
  reacc: 'no',
  aeroOnComments: null,
  askStop: 'no',
  rain: null,
  askAnillado: 'no',
  aeroOn: 'no',
  sex: null,
  age: null,
  flightDirection: null,
  birdReaction: null,
  flightType: null,
  anillado: null,
  photos: [] as File[],
  isProtected: false,
  censusMin: null,
  extraAnillado: null,
});

const OfcEditTabs = ({ fromPark }: { fromPark?: boolean }) => {

  // PROVIDER

  const { updateWindTurbineHookFilters } = useCensusFormContext();
 
  // STATES

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const { id = '', parkId = null } = useParams<{ id: string, parkId?: string }>();
  const [censusLines, setCensusLines] = useState<Record<string, FormikValues>>({});
  const [censusTimeBounds, setCensusTimeBounds] = useState<{ start?: string; end?: string }>({});
  const [tabs, setTabs] = useState<TabData[]>([
    {
      title: "Datos Generales",
      description: "Información básica del OFC",
      isValid: true,
      isLine: false,
      formikValues: {
        parkId: '',
        userId: '',
      },
      formikErrors: {},
      formikTouched: {}
    }
  ]);

  const breadCrumb = [
    { name: 'Administración' },
    fromPark ? { name: 'Gestión de OFCs en parque', href: `/parks/${parkId}/profile/ofc` } : { name: 'Gestión de OFCs', href: '/ofc' },
    { name: 'Actualizar OFC' },
  ];

  // HOOKS

  const navigate = useNavigate();
  const censusService = new CensusService();
  const { userCan } = usePrivilege();

  // FORMIK GENERAL DATA

  const generalDataFormik = useFormik({
    initialValues: tabs[0].formikValues,
    validationSchema: userCan('list', 'companies') ? SuperAdminSchemaOFC : generalDataValidationSchemaOFC,
    onSubmit: () => {},
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: true,
  });

  // FORMIK LINE

  const currentLineFormik = useFormik({
    initialValues: (() => {

      // IF NOT LINE TAB RETURN INITIAL VALUES


      if (activeTabIndex <= 0 || !tabs[activeTabIndex]) {
        return getInitialLineValues();
      }
      
      // GET LINE ID FROM TAB
      
      const currentTab = tabs[activeTabIndex];
      const lineId = currentTab.lineId;
      
      if (lineId && typeof lineId === 'string' && lineId in censusLines) {
        return JSON.parse(JSON.stringify(censusLines[lineId]));
      }
      
      return JSON.parse(JSON.stringify(currentTab.formikValues));
    })(),
    validationSchema: createLineValidationSchema(censusTimeBounds.start, censusTimeBounds.end),
    onSubmit: () => {},
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnChange: false,
    initialTouched: activeTabIndex > 0 && tabs[activeTabIndex] ? 
      {...tabs[activeTabIndex].formikTouched} : 
      {},
  });

  // FUNCTIONS

  // ---------------------------------------------------------------------------------
  /**
   * @ES MANEJA EL CAMBIO DE PESTAÑA
   * @EN HANDLES TAB CHANGE
   * @param index 
   */
  // ---------------------------------------------------------------------------------
  const handleTabChange = async (index: number) => {
    if (activeTabIndex === 0) {
      setTabs(prev => {
        const updated = [...prev];
        updated[0].formikValues = generalDataFormik.values;
        updated[0].formikTouched = {...generalDataFormik.touched};
        return updated;
      });
    } else if (activeTabIndex > 0 && activeTabIndex < tabs.length) {
      setTabs(prev => {
        const updated = [...prev];
        updated[activeTabIndex].formikValues = currentLineFormik.values;
        updated[activeTabIndex].formikTouched = {...currentLineFormik.touched};
        
        if (updated[activeTabIndex].lineId) {
          setCensusLines(prev => ({
            ...prev,
            [updated[activeTabIndex].lineId!]: currentLineFormik.values
          }));
        }
        return updated;
      });
    }
    
    setActiveTabIndex(index);
  };
  // ---------------------------------------------------------------------------------

  // ---------------------------------------------------------------------------------
  /**
   * @ES AÑADE UNA NUEVA PESTAÑA DE LÍNEA
   * @EN ADDS A NEW LINE TAB
   */
  // ---------------------------------------------------------------------------------
  const handleAddLine = async () => {
    const lineId = `line_${tabs.length}`;
    
    const newTabIndex = tabs.length;

    // GET PREV LINE TAB IF EXISTS

    const prevLineTab = tabs[newTabIndex - 1];
    const prevLineValues = prevLineTab ? prevLineTab.formikValues : {};

    const newLineValues = {
      specieId: null,
      turbineId:null,
      windDirection: prevLineValues.windDirection || null,
      windSpeed: prevLineValues.windSpeed || null,
      cloudCoverage: prevLineValues.cloudCoverage || null,
      visibility: prevLineValues.visibility || null,
      numIndividuals: null,
      aeroStatus: 'encendido',
      altitudLevel: null,
      distanceToAero: null,
      riskDetected: false,
      isOfc: 'false',
      dirOrigen: null,
      dirDest: null,
      reacc: 'no',
      aeroOnComments: null,
      askStop: null,
      rain: prevLineValues.rain || null,
      askAnillado: null,
      sex: '0',
      age: '0',
      flightDirection: null,
      birdReaction: null,
      flightType: null,
      anillado: null,
      photos: null as File[] | null,
      isProtected: false,
      censusMin: null,
      time: null,
      extraAnillado: null,
    };
    
    setTabs([
      ...tabs,
      {
        title: `Línea ${newTabIndex}`,
        description: "-",
        isLine: true,
        isValid: true,
        formikValues: newLineValues,
        formikErrors: {},
        formikTouched: {},
        lineId: lineId
      }
    ]);
    
    setCensusLines(prev => ({
      ...prev,
      [lineId]: {...newLineValues}
    }));
    
    setActiveTabIndex(newTabIndex);
  };
  // ---------------------------------------------------------------------------------

  // ---------------------------------------------------------------------------------
  /**
   * @ES ELIMINA UNA PESTAÑA DE LÍNEA
   * @EN DELETES A LINE TAB
   * @param index 
   */
  // ---------------------------------------------------------------------------------
  const handleDeleteLine = (index: number) => {
    if (index === 0) {
      toast.error("No puedes eliminar la pestaña de datos generales");
      return;
    }
    
    const lineIdToDelete = tabs[index].lineId;
    
    setTabs(prev => {
      const updated = [...prev];
      updated.splice(index, 1);
      
      for (let i = 1; i < updated.length; i++) {
        updated[i].title = `Línea ${i}`;
      }
      
      return updated;
    });
    
    if (lineIdToDelete) {
      setCensusLines(prev => {
        const updated = {...prev};
        delete updated[lineIdToDelete];
        return updated;
      });
    }
    
    if (index === activeTabIndex) {
      setActiveTabIndex(index - 1);
    } else if (index < activeTabIndex) {
      setActiveTabIndex(activeTabIndex - 1);
    }
  };
  // ---------------------------------------------------------------------------------

  // ---------------------------------------------------------------------------------
  /**
   * @ES FINALIZA EL CENSO
   * @EN FINISHES THE CENSUS
   */
  // ---------------------------------------------------------------------------------
  const handleFinish = async () => {

    // UPDATE VALUES OF TABS

    if (activeTabIndex === 0) {
      setTabs(prev => {
        const updated = [...prev];
        updated[0].formikValues = generalDataFormik.values;
        return updated;
      });
    } else if (activeTabIndex > 0 && activeTabIndex < tabs.length) {
      setTabs(prev => {
        const updated = [...prev];
        updated[activeTabIndex].formikValues = currentLineFormik.values;
        
        if (updated[activeTabIndex].lineId) {
          setCensusLines(prev => ({
            ...prev,
            [updated[activeTabIndex].lineId!]: currentLineFormik.values
          }));
        }
        return updated;
      });
    }
    
    // VALIDATE ALL TABS

    // FIRST VALIDATE GENERAL DATA

    const generalErrors = await generalDataFormik.validateForm();
    
    const generalTouched = Object.keys(generalDataFormik.values).reduce<Record<string, boolean>>((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    
    generalDataFormik.setTouched(generalTouched);
    
    let hasErrors = Object.keys(generalErrors).length > 0;
    
    // UPDATE STATUS OF GENERAL DATA TAB

    setTabs(prev => {
      const updated = [...prev];
      updated[0] = {
        ...updated[0],
        isValid: Object.keys(generalErrors).length === 0,
        formikErrors: generalErrors,
        formikTouched: generalTouched
      };
      return updated;
    });
    
    // THEN VALIDATE ALL LINE TABS

    for (let i = 1; i < tabs.length; i++) {
      const touchedFields = Object.keys(tabs[i].formikValues).reduce<Record<string, boolean>>((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      
      try {
        await createLineValidationSchema(
          censusTimeBounds.start,
          censusTimeBounds.end
        ).validate(tabs[i].formikValues, { abortEarly: false });
        
        setTabs(prev => {
          const updated = [...prev];
          updated[i] = {
            ...updated[i],
            isValid: true,
            formikErrors: {},
            formikTouched: touchedFields
          };
          return updated;
        });
        
        // Si esta es la pestaña activa, también actualizamos el formik actual
        if (i === activeTabIndex) {
          currentLineFormik.setTouched(touchedFields);
        }
      } catch (validationError) { 
        const errors: Record<string, string> = {};
        if (validationError instanceof yup.ValidationError) {
          validationError.inner.forEach(err => {
            if (err.path) {
              errors[err.path] = err.message;
            }
          });
        }
        
        // UPDATE STATUS OF THIS LINE TAB
        setTabs(prev => {
          const updated = [...prev];
          updated[i] = {
            ...updated[i],
            isValid: false,
            formikErrors: errors,
            formikTouched: touchedFields
          };
          return updated;
        });
        
        // Si esta es la pestaña activa, también actualizamos el formik actual
        if (i === activeTabIndex) {
          currentLineFormik.setTouched(touchedFields);
        }
        
        hasErrors = true;
      }
    }
    
    // IF THERE ARE ERRORS, SHOW A TOAST AND EXIT
    if (hasErrors) {
      toast.error("Hay pestañas con errores. Por favor, revísalas antes de finalizar.");
      return;
    }
    
    // THEN PREPARE CENSUS DATA TO SEND 
    
    const formData = new FormData();
    
    // Append general census data
    if (userCan('list', 'companies')) {
      formData.append('company', generalDataFormik.values.company?.toString() || "");
    }
    formData.append('id', id || "");
    formData.append('statusId', generalDataFormik.values.statusId?.toString() || "");
    formData.append('parkId', generalDataFormik.values.parkId?.toString() || "");
    formData.append('userId', generalDataFormik.values.userId?.toString() || "");
    formData.append('isOfc', "true");
    
    // Add census lines data
    Object.values(censusLines).forEach((line: any, index: number) => {
      const lineData = {
        id: line.id?.toString() || "",
        specieId: line.specieId?.toString() || "",
        windTurbineId: line.turbineId?.toString() || "",
        windDirection: line.windDirection?.toString() || "",
        windSpeed: line.windSpeed?.toString() || "",
        cloudCoverage: line.cloudCoverage?.toString() || "",
        visibility: line.visibility?.toString() || "",
        numIndividuals: line.numIndividuals?.toString() || "",
        aeroStatus: line.aeroStatus?.toString() || "",
        altitudLevel: line.altitudLevel?.toString() || "",
        distanceToAero: line.distanceToAero?.toString() || "",
        riskDetected: line.riskDetected?.toString() || "",
        isOfc: 'false',
        dirOrigen: line.dirOrigen?.toString() || "",
        dirDest: line.dirDest?.toString() || "",
        reacc: line.reacc?.toString() || "",
        aeroOnComments: line.aeroOnComments?.toString() || "",
        askStop: line.askStop?.toString() || "",
        rain: line.rain?.toString() || "",
        askAnillado: line.askAnillado?.toString() || "",
        sex: line.sex?.toString() || "",
        age: line.age?.toString() || "",
        flightDirection: line.flightDirection?.toString() || "",
        birdReaction: line.birdReaction?.toString() || "",
        flightType: line.flightType?.toString() || "",
        anillado: line.anillado?.toString() || "",
        isProtected: line.isProtected?.toString() || "",
        censusMin: line.censusMin?.toString() || "",
        time: line.time?.toString() || "",
        extraAnillado: line.extraAnillado?.toString() || "",
      };
      
      // Add each field of the census line to FormData
      Object.entries(lineData).forEach(([key, value]) => {
        formData.append(`censusLines[${index}][${key}]`, value);
      });
      
      // Add line photos if they exist
      if (line.photos && Array.isArray(line.photos)) {
        line.photos.forEach((photo: File, photoIndex: number) => {
          formData.append(`censusLines_${index}_photos_${photoIndex}`, photo);
        });
      }
    });

    // SEND DATA
    
    try {
      const response = await censusService.editCensus(formData);
      const result = await response.getResponseData();
      
      if (result.success) {
        toast.success("OFC editado correctamente");
        if (fromPark) {
          navigate(`/parks/${parkId}/profile/ofc`);
        } else {
          navigate("/ofc");
        }
      } else {
        toast.error("Error al editar el OFC: " + result.message);
      }
    } catch (error) {
      console.error("Error al editar el OFC:", error);
      toast.error("Error al editar el OFC");
    }
  };
  // ---------------------------------------------------------------------------------

  // ---------------------------------------------------------------------------------
  /**
   * @ES ACTUALIZA LOS TABS CON LOS DATOS CARGADOS
   * @EN UPDATES TABS WITH LOADED DATA
   * @param linesData 
   */
  // ---------------------------------------------------------------------------------
  const updateTabsFromData = (linesData: { [key: string]: any }) => {
    const newTabs: TabData[] = [
      {
        title: "Datos Generales",
        description: "Información básica del OFC",
        isValid: true,
        isLine: false,
        formikValues: generalDataFormik.values,
        formikErrors: {},
        formikTouched: {}
      }
    ];

    // ADD TABS FOR EACH OFC LINE

    Object.keys(linesData).forEach((lineId, index) => {
      newTabs.push({
        title: `Línea ${index + 1}`,
        description: "-",
        isLine: true,
        isValid: true,
        formikValues: linesData[lineId],
        formikErrors: {},
        formikTouched: {},
        lineId: lineId
      });
    });

    setTabs(newTabs);
  };
  // ---------------------------------------------------------------------------------

  // USE EFFECTS 

  useEffect(() => {
    const loadOfcData = async () => {
    
      if (!id) return; // IF ID IS NOT PROVIDED, EXIT
    
      try {
        const response = await censusService.getCensusById(id);
        const result = await response.getResponseData();
        
        if (result.success && result.data) {

          const censusData = result.data;

          const formatCensusTime = (value: { date?: string } | string | null | undefined) => {
            if (!value) return '';
            if (typeof value === 'string') {
              const parsed = moment(value, 'HH:mm', true);
              return parsed.isValid() ? parsed.format('HH:mm') : '';
            }
            const parsed = moment(value.date);
            return parsed.isValid() ? parsed.format('HH:mm') : '';
          };

          setCensusTimeBounds({
            start: formatCensusTime(censusData.time),
            end: formatCensusTime(censusData.endTime),
          });
          
          // SET CENSUS GENERAL DATA

          generalDataFormik.setValues({
            ...(userCan('list', 'companies') ? { company: censusData.company?.id || '' } : {}),
            parkId: censusData.park?.id || '',
            statusId: censusData.status?.id || '',
            userId: censusData.user?.id || '',
          });

          // SET CENSUS LINES DATA

          const linesData: { [key: string]: any } = {};
          if (censusData.censusLines && censusData.censusLines.length > 0) {
            for (const [index, line] of censusData.censusLines.entries()) {
              const lineId = `line-${index + 1}`;
              
              // Filter photos for this line from the general photos array
              const linePhotos = censusData.censusPhotos ? censusData.censusPhotos.filter((photo: any) => 
                photo.censusLine && photo.censusLine.id === line.id
              ) : [];
              
              // Convert photos to File objects
               const photoFiles: File[] = [];
               for (const photo of linePhotos) {
                 try {
                   const documentsService = new DocumentsService();
                   const response = await documentsService.renderDocument(photo.document.id);
                   const blob = response.getResponseData();
                   const file = new File([blob], photo.document.originalName, { type: blob.type });
                   photoFiles.push(file);
                 } catch (error) {
                   console.error(`Error loading photo ${photo.document.originalName}:`, error);
                 }
               }
              
              linesData[lineId] = {
                specieId: line.species?.id || null,
                turbineId: line.windturbine?.id || null,
                ...normalizeWindFieldsForForm(line.windDirection, line.windSpeed),
                cloudCoverage: String(line.cloudCoverage) || null,
                visibility: String(line.visibility) || null,
                numIndividuals: line.numIndividuals || null,
                aeroStatus: line.aeroStatus || 'encendido',
                altitudLevel: line.altitudLevel || null,
                distanceToAero: line.distanceToAero || null,
                riskDetected: line.riskDetected || false,
                isOfc: line.isOfc || 'false',
                dirOrigen: line.dirOrigen || null,
                dirDest: line.dirDest || null,
                reacc: line.reacc || null,
                aeroOnComments: line.aeroOnComments || null,
                askStop: line.askStop || 'no',
                rain: line.rain || null,
                askAnillado: line.askAnillado,
                aeroOn: 'no',
                sex: line.sex || null,
                age: line.age || null,
                flightDirection: line.flightDirection || null,
                birdReaction: line.birdReaction || null,
                flightType: line.flightType || null,
                anillado: line.anillado || null,
                photos: photoFiles,
                isProtected: (line.species?.catAndalucia || line.species?.catNacional) ? true : false, 
                censusMin: line.censusMin || null,
                time: line.time?.date ? (() => {
                    const timeMoment = moment(line.time.date);
                    return timeMoment.isValid() ? timeMoment.format('HH:mm') : '';
                })() : '',
                extraAnillado: line.extraAnillado || null,
              };
            }
          }
          
          setCensusLines(linesData);
          updateTabsFromData(linesData);
        } else {
          toast.error("Error al cargar el OFC");
          navigate('/ofc');
        }
      } catch (error) {
        console.error("Error loading ofc:", error);
        toast.error("Error al cargar el OFC");
        navigate('/ofc');
      }
    };

    loadOfcData();
  }, [id]);

  useEffect(() => {
    if (parkId != null) {
      updateWindTurbineHookFilters({
        filter_filters: {
          park: parkId,
        },
      });
    } else if (generalDataFormik.values.parkId != null) {
      updateWindTurbineHookFilters({
        filter_filters: {
          park: generalDataFormik.values.parkId,
        },
      });
    }
  }, [parkId, generalDataFormik.values.parkId]);
  
  // RENDER

  return (
    <>
      <PageHeader title="Actualizar OFC" breadcrumb={breadCrumb} />
      
      <div className="p-6 bg-white rounded-lg shadow-sm">
        <TabGroup selectedIndex={activeTabIndex} onChange={handleTabChange}>
          <div className="flex justify-between items-center mb-6">
            <TabList className="flex space-x-1 overflow-x-auto p-1 bg-gray-100 rounded-lg">
              {tabs.map((tab, index) => (
                <Tab
                  key={index + `${tab.isValid}`}
                  className={({ selected }) =>
                    `py-2.5 px-4 text-sm font-medium rounded-md whitespace-nowrap
                    ${selected
                      ? 'text-primary shadow bg-white'
                      : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'}
                    ${!tab.isValid ? 'border-2 border-red-500 text-red' : ''}
                    `
                  }
                >
                  <div className="flex items-center">
                    { tab.isLine ? <LuClipboardList /> : <PiBird />}
                    <span className="ms-2 me-2">{tab.title}</span>
                    {!tab.isValid ? <MdErrorOutline className="text-red"/> : <TbRosetteDiscountCheck className="text-success" />}
                    {index > 0 && (
                      <button
                        type="button"
                        className="ml-2 text-gray-400 hover:text-red-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteLine(index);
                        }}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    )}
                  </div>
                </Tab>
              ))}
              
              <button
                type="button"
                className="py-2.5 px-4 text-sm font-medium text-gray-700 hover:bg-gray-200 hover:text-gray-900 rounded-md whitespace-nowrap"
                onClick={handleAddLine}
              >
                <div className="flex items-center">
                  <FiPlus size={16} className="mr-1" />
                  <span>Añadir línea</span>
                </div>
              </button>
            </TabList>
          </div>
          
          <TabPanels className="mt-4">

            {/* GENERAL DATA */}

            <TabPanel>
              <OfcGeneralDataForm
                formik={generalDataFormik}
                isEdit={true}
                parkId={parkId}
                onValidationChange={(isValid) => {
                  setTabs(prev => {
                    const updated = [...prev];
                    updated[0].isValid = isValid;
                    return updated;
                  });
                }}
              />
            </TabPanel>
            
            {/* OFC LINES */}
            {tabs.slice(1).map((tab, index) => (
              <TabPanel key={index + 1}>
                <CensusLineForm 
                  key={tab.lineId}
                  initialValues={tab.formikValues}
                  lineNumber={index + 1}
                  lineId={tab.lineId}
                  parkId={generalDataFormik.values.parkId}
                  censusStartTime={censusTimeBounds.start}
                  censusEndTime={censusTimeBounds.end}
                  initialTouched={tab.formikTouched}
                  onValidationChange={(isValid) => {
                    setTabs(prev => {
                      const updated = [...prev];
                      updated[index + 1].isValid = isValid;
                      return updated;
                    });
                  }}
                  onFormChange={(values) => {
                    setTabs(prev => {
                      const updated = [...prev];
                      updated[index + 1].formikValues = values;
                      return updated;
                    });
                    
                    if (tab.lineId) {
                      setCensusLines(prev => ({
                        ...prev,
                        [tab.lineId!]: values
                      }));
                    }
                    
                    if (index + 1 === activeTabIndex) {
                      currentLineFormik.setValues(values);
                    }
                  }}
                />
              </TabPanel>
            ))}
          </TabPanels>
        </TabGroup>
        
        <div className="flex justify-end mt-8 space-x-4">
          <Button
            variant="outline"
            onClick={() => {
              if (fromPark) {
                navigate(`/parks/${parkId}/profile/ofc`);
              } else {
                navigate('/ofc');
              }
            }}
          >
            Cancelar
          </Button>
          
          <Button
            variant="solid"
            onClick={handleFinish}
          >
            Actualizar OFC
          </Button>
        </div>
      </div>
    </>
  );
};

export default OfcEditTabs;
